/* eslint-disable no-console */
import { call, put, takeEvery, select, take } from 'redux-saga/effects'

import generalActions from 'models/General/actions'
import generalSelectors from 'models/General/selectors'
import profileActions from 'models/ProfileInfo/actions'

import {
  initAmplify,
  handleSignIn,
  handleSignOut,
  fetchIsUserAuthenticated,
  handleConfirmSignIn,
} from 'services/amplify'
import { setUserId } from 'services/analytics/amplitude'
import { intercomShutdown } from 'services/Intercom'
import { branchLogOut } from 'services/branch/branchService'
import { setSentryUser } from 'services/Sentry'

import { formatEmailAddress } from 'utils/email'
import { formatPhoneNumber } from 'utils/phone-number'
import { sendAuthenticationCode } from 'utils/authentication'

import actions from './actions'

export function* initializeAuth() {
  try {
    yield call(initAmplify)
    yield put(actions.initializeAuth.success())
  } catch (error) {
    console.error('[Authentication] initializeAuth:', error)
    yield put(actions.initializeAuth.failure())
  }
}

export function* logInUser(action) {
  const { username, isEmail, nextUrl, callback } = action.payload
  const region = yield select(generalSelectors.getRegion)

  let formattedUsername = null
  if (isEmail) {
    formattedUsername = yield call(formatEmailAddress, username)
  } else {
    formattedUsername = yield call(formatPhoneNumber, username, region, true)
  }
  let isSuccess = false
  let message = null
  try {
    const { data } = yield call(sendAuthenticationCode, formattedUsername, nextUrl)
    const authCodeUserAlias = data?.userAlias || ''
    yield put(actions.logInUser.success(formattedUsername, authCodeUserAlias))
    isSuccess = true
  } catch (error) {
    console.error('[Authentication] logInUser', error)
    const { code, message: cognitoMessage, name } = error || {}
    message = cognitoMessage
    yield put(actions.logInUser.failure(code, message, name))
  } finally {
    if (callback) {
      yield call(callback, isSuccess, message)
    }
  }
}

export function* confirmLogInUser(action) {
  const { code, userAlias, callback } = action.payload

  let isSuccess = false
  let message = null
  try {
    yield call(handleSignIn, userAlias)
    const isSignedIn = yield call(handleConfirmSignIn, code)
    if (!isSignedIn) {
      throw new Error('Failed to confirm sign in')
    }
    yield put(profileActions.fetchProfileInfo.init())
    const resultAction = yield take([
      profileActions.fetchProfileInfo.SUCCESS_TYPE,
      profileActions.fetchProfileInfo.FAILURE_TYPE,
    ])

    if (resultAction.type === profileActions.fetchProfileInfo.FAILURE_TYPE) {
      throw new Error('Failed to fetch profile info')
    }
    yield put(actions.confirmLogInUser.success())
    isSuccess = true
  } catch (error) {
    console.error('[Authentication] confirmLogInUser', error)
    const { code: errorCode, message: cognitoMessage, name } = error || {}
    message = cognitoMessage
    yield put(actions.confirmLogInUser.failure(errorCode, message, name))
  } finally {
    if (callback) {
      yield call(callback, isSuccess, message)
    }
  }
}

export function* signOutUser({ payload }) {
  const { callback } = payload
  let isSuccess = false

  try {
    const result = yield call(handleSignOut)
    yield put(actions.signOutUser.success(result))
    yield call(intercomShutdown)
    yield call(branchLogOut)
    yield call(setSentryUser, null)
    yield call(setUserId, null)
    isSuccess = true
    yield put({ type: 'RESET' })
    yield put(generalActions.initApp.init())
  } catch (error) {
    console.error('[Authentication] signOutUser:', error)
    yield put(actions.signOutUser.failure(error))
  } finally {
    if (callback) {
      yield call(callback, isSuccess)
    }
  }
}

export function* checkIsAuthenticated() {
  const isAppInitialized = yield select(generalSelectors.getIsAppInitialized)

  let result
  if (!isAppInitialized) {
    result = false
  } else {
    result = yield call(fetchIsUserAuthenticated)
  }

  yield put(actions.checkIsAuthenticated.success(result))
}

export default function* main() {
  yield takeEvery(actions.initializeAuth.INIT_TYPE, initializeAuth)
  yield takeEvery(actions.logInUser.INIT_TYPE, logInUser)
  yield takeEvery(actions.confirmLogInUser.INIT_TYPE, confirmLogInUser)
  yield takeEvery(actions.signOutUser.INIT_TYPE, signOutUser)
  yield takeEvery(actions.checkIsAuthenticated.INIT_TYPE, checkIsAuthenticated)
}
